import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Monitor, Smartphone, Square, Columns, LayoutTemplate, Plus } from 'lucide-react'

export interface SizeCategory {
  label: string
  icon: React.ReactNode
  sizes: { label: string; dimensions: string; desc?: string }[]
}

export const SIZE_CATEGORIES: SizeCategory[] = [
  {
    label: 'Display',
    icon: <Monitor className="h-4 w-4" />,
    sizes: [
      { label: 'Medium Rectangle', dimensions: '300x250', desc: 'Most popular' },
      { label: 'Leaderboard', dimensions: '728x90' },
      { label: 'Wide Skyscraper', dimensions: '160x600' },
      { label: 'Half Page', dimensions: '300x600' },
      { label: 'Billboard', dimensions: '970x250' },
      { label: 'Large Leaderboard', dimensions: '970x90' },
      { label: 'Banner', dimensions: '468x60' },
    ],
  },
  {
    label: 'Mobile',
    icon: <Smartphone className="h-4 w-4" />,
    sizes: [
      { label: 'Mobile Banner', dimensions: '320x50' },
      { label: 'Mobile Interstitial', dimensions: '320x480' },
      { label: 'Large Mobile Banner', dimensions: '320x100' },
    ],
  },
  {
    label: 'Social',
    icon: <Square className="h-4 w-4" />,
    sizes: [
      { label: 'Square Post', dimensions: '1080x1080', desc: 'Instagram / Facebook' },
      { label: 'Landscape Post', dimensions: '1200x628', desc: 'Facebook / LinkedIn' },
      { label: 'Story / Reel', dimensions: '1080x1920', desc: 'Instagram / TikTok' },
      { label: 'Twitter Post', dimensions: '1200x675' },
      { label: 'Pinterest Pin', dimensions: '1000x1500' },
    ],
  },
  {
    label: 'Video',
    icon: <Columns className="h-4 w-4" />,
    sizes: [
      { label: 'HD Landscape', dimensions: '1920x1080' },
      { label: 'HD Portrait', dimensions: '1080x1920' },
      { label: 'Square Video', dimensions: '1080x1080' },
    ],
  },
]

interface SizePickerModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (sizes: string[]) => void
}

export function SizePickerModal({ isOpen, onClose, onConfirm }: SizePickerModalProps) {
  const [selectedSizes, setSelectedSizes] = useState<string[]>([])
  const [customW, setCustomW] = useState('')
  const [customH, setCustomH] = useState('')
  const [showCustom, setShowCustom] = useState(false)

  const toggle = (dim: string) => {
    setSelectedSizes(prev =>
      prev.includes(dim) ? prev.filter(s => s !== dim) : [...prev, dim]
    )
  }

  const addCustom = () => {
    const w = parseInt(customW)
    const h = parseInt(customH)
    if (w > 0 && h > 0) {
      const dim = `${w}x${h}`
      if (!selectedSizes.includes(dim)) setSelectedSizes(prev => [...prev, dim])
      setCustomW('')
      setCustomH('')
      setShowCustom(false)
    }
  }

  const handleConfirm = () => {
    if (selectedSizes.length > 0) onConfirm(selectedSizes)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.4 }}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                    <LayoutTemplate className="h-3 w-3" />
                    Sizes
                  </div>
                  <h2 className="text-2xl font-bold mt-1">Choose creative size</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Select one or more sizes for your creative.
                  </p>
                </div>
                <button onClick={onClose} className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shadow-sm" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-4 space-y-5" style={{ maxHeight: 'calc(85vh - 140px)' }}>
              {SIZE_CATEGORIES.map(cat => (
                <div key={cat.label}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-gray-400 dark:text-gray-500">{cat.icon}</span>
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{cat.label}</h3>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {cat.sizes.map(size => {
                      const active = selectedSizes.includes(size.dimensions)
                      const [w, h] = size.dimensions.split('x').map(Number)
                      const ratio = w / h
                      const previewW = Math.min(40, 40 * ratio)
                      const previewH = Math.min(40, 40 / ratio)
                      return (
                        <button
                          key={size.dimensions}
                          onClick={() => toggle(size.dimensions)}
                          className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all text-left ${
                            active
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-sm shadow-primary-200 dark:shadow-primary-900/40'
                              : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-950'
                          }`}
                        >
                          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                            <div
                              className={`rounded-sm border transition-colors ${
                                active
                                  ? 'border-primary-400 bg-primary-100 dark:bg-primary-800/40'
                                  : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                              }`}
                              style={{ width: `${previewW}px`, height: `${previewH}px` }}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className={`text-xs font-medium truncate ${active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                              {size.label}
                            </p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">{size.dimensions}px</p>
                            {size.desc && <p className="text-[10px] text-gray-400 dark:text-gray-500">{size.desc}</p>}
                          </div>
                          {active && (
                            <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
                              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}

              {/* Custom size */}
              <div>
                <div className="flex items-center gap-2 mb-2.5">
                  <Plus className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Custom Size</h3>
                </div>
                {showCustom ? (
                  <div className="flex items-center gap-2">
                    <input type="number" value={customW} onChange={e => setCustomW(e.target.value)} placeholder="Width" min={1}
                      className="w-24 px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <span className="text-gray-400 text-sm">x</span>
                    <input type="number" value={customH} onChange={e => setCustomH(e.target.value)} placeholder="Height" min={1}
                      onKeyDown={e => e.key === 'Enter' && addCustom()}
                      className="w-24 px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    <button onClick={addCustom} className="px-3 py-2 text-sm font-medium rounded-xl bg-primary-600 text-white hover:bg-primary-500 transition-colors">Add</button>
                    <button onClick={() => setShowCustom(false)} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowCustom(true)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all text-sm">
                    <Plus className="h-4 w-4" />
                    Add custom size
                  </button>
                )}
              </div>

              {/* Selected summary */}
              {selectedSizes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {selectedSizes.map(s => (
                    <span key={s} onClick={() => toggle(s)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors">
                      {s}<X className="h-3 w-3" />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-gray-950">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {selectedSizes.length === 0
                  ? 'Select at least one size to continue'
                  : `${selectedSizes.length} size${selectedSizes.length !== 1 ? 's' : ''} selected`}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={selectedSizes.length === 0}
                  className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${
                    selectedSizes.length === 0
                      ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-500 shadow-sm hover:shadow-md'
                  }`}>
                  Start Creating
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
